import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';

const DataContext = createContext({
  settings: null,
  categories: [],
  featuredPackages: [],
  isInitialLoading: true,
});

// Holds Firestore subscriptions at the app root so navigating between routes
// does not re-fetch the same data (and does not re-trigger the landing loader).
export function DataProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);

  // Track which subscriptions have delivered their first snapshot.
  // Initial loader is hidden once all of them have responded at least once.
  const [hasSettings, setHasSettings] = useState(false);
  const [hasCategories, setHasCategories] = useState(false);
  const [hasPackages, setHasPackages] = useState(false);

  useEffect(() => {
    const unsubscribeSettings = onSnapshot(
      doc(db, 'settings', 'general'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSettings(snapshot.data());
        }
        setHasSettings(true);
      },
      () => setHasSettings(true)
    );

    const unsubscribeCategories = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        setCategories(
          snapshot.docs.map((d) => ({ firestoreId: d.id, ...d.data() }))
        );
        setHasCategories(true);
      },
      () => setHasCategories(true)
    );

    const featuredQuery = query(
      collection(db, 'packages'),
      where('prikaziNaNaslovnoj', '==', true)
    );
    const unsubscribePackages = onSnapshot(
      featuredQuery,
      (snapshot) => {
        setFeaturedPackages(
          snapshot.docs.map((d) => ({ firestoreId: d.id, ...d.data() }))
        );
        setHasPackages(true);
      },
      () => setHasPackages(true)
    );

    return () => {
      unsubscribeSettings();
      unsubscribeCategories();
      unsubscribePackages();
    };
  }, []);

  const isInitialLoading = !(hasSettings && hasCategories && hasPackages);

  return (
    <DataContext.Provider
      value={{ settings, categories, featuredPackages, isInitialLoading }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
